import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { enrollmentsApi, certificatesApi } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { MagnifyingGlass, CaretLeft, CaretRight, DownloadSimple, CheckCircle, XCircle, Clock, FileXls, Eraser, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CourseCountBadge from '../../components/CourseCountBadge';

const ITEMS_PER_PAGE = 10;
const ALL = 'all';
const EMPTY_FILTERS = { training: ALL, cargo: ALL, status: ALL, result: ALL, dateFrom: '', dateTo: '' };

const resultOf = (e) => {
  if (e.results_locked) return 'Bloqueado';
  if (e.resultado) return e.resultado;
  if (e.score === null || e.score === undefined) return 'Pendiente';
  return e.score >= 60 ? 'Aprobado' : 'Reprobado';
};
const statusOf = (e) => (e.status === 'pending' ? 'Pendiente' : e.status === 'rejected' ? 'Rechazado' : 'Inscrito');
const dayOf = (e) => (e.training_scheduled_date ? e.training_scheduled_date.slice(0, 10) : '');
const fmtDate = (e) => (e.training_scheduled_date ? new Date(e.training_scheduled_date).toLocaleDateString('es-CL') : '-');
const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b));

const FilterSelect = ({ testId, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid={testId}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default function ContractorResultsPage() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [preview, setPreview] = useState({ open: false, url: '', cert: null, title: '' });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const enrollRes = await enrollmentsApi.getAll();
      setEnrollments(enrollRes.data);
    } catch { toast.error('Error al cargar resultados'); }
    finally { setLoading(false); }
  };

  const findCert = async (e) => {
    const res = await certificatesApi.getAll({ worker_id: e.worker_id, training_id: e.training_id });
    return res.data[0];
  };

  const handleViewCert = async (e) => {
    setPreview({ open: true, url: '', cert: null, title: `${e.worker_name} · ${e.training_name}` });
    try {
      const cert = await findCert(e);
      if (!cert) { toast.error('Certificado no disponible aún'); setPreview(p => ({ ...p, open: false })); return; }
      const res = await certificatesApi.preview(cert.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreview(p => ({ ...p, url, cert }));
    } catch {
      toast.error('Error al cargar el certificado');
      setPreview(p => ({ ...p, open: false }));
    }
  };

  const handleDownloadCert = async () => {
    const cert = preview.cert;
    if (!cert) return;
    setDownloading(true);
    try {
      const res = await certificatesApi.download(cert.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = `certificado_${cert.code}.pdf`;
      link.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar certificado'); }
    finally { setDownloading(false); }
  };

  const closePreview = () => {
    if (preview.url) window.URL.revokeObjectURL(preview.url);
    setPreview({ open: false, url: '', cert: null, title: '' });
  };

  const trainingOptions = useMemo(() => uniq(enrollments.map(e => e.training_name)), [enrollments]);
  const cargoOptions = useMemo(() => uniq(enrollments.map(e => e.worker_cargo)), [enrollments]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return enrollments.filter(e => {
      const d = dayOf(e);
      return (
        ((e.worker_name || '').toLowerCase().includes(q) || (e.training_name || '').toLowerCase().includes(q) || (e.worker_rut || '').includes(searchTerm)) &&
        (filters.training === ALL || e.training_name === filters.training) &&
        (filters.cargo === ALL || e.worker_cargo === filters.cargo) &&
        (filters.status === ALL || statusOf(e) === filters.status) &&
        (filters.result === ALL || resultOf(e) === filters.result) &&
        (!filters.dateFrom || (d && d >= filters.dateFrom)) &&
        (!filters.dateTo || (d && d <= filters.dateTo))
      );
    });
  }, [enrollments, searchTerm, filters]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, filters]);

  const setFilter = (key) => (value) => setFilters(f => ({ ...f, [key]: value }));
  const hasFilters = searchTerm || JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const handleExportExcel = () => {
    if (filtered.length === 0) { toast.error('No hay registros para exportar con los filtros actuales'); return; }
    const rows = filtered.map(e => {
      const r = resultOf(e);
      return {
        'Trabajador': e.worker_name || '',
        'RUT': e.worker_rut || '',
        'Cargo': e.worker_cargo || '',
        'Empresa': e.company_name || user?.company_name || '',
        'Capacitación': e.training_name || '',
        'Fecha': fmtDate(e),
        'Estado': statusOf(e),
        'Asistencia': e.attended ? 'Sí' : 'No',
        'Resultado': r === 'Bloqueado' ? 'Disponible a las 14:00 hrs' : r,
        'Puntaje (%)': e.results_locked || e.score === null || e.score === undefined ? '' : e.score,
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [32, 14, 30, 24, 48, 12, 12, 12, 24, 12].map(wch => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resultados');
    XLSX.writeFile(wb, `resultados_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Excel descargado (${rows.length} registros)`);
  };

  const getResultBadge = (e) => {
    const r = resultOf(e);
    if (r === 'Bloqueado') {
      return <Badge className="bg-blue-50 text-blue-600 border-blue-200 border" data-testid={`result-locked-${e.id}`}><Clock size={12} className="mr-1" /> Disponible a las 14:00 hrs</Badge>;
    }
    if (r === 'Pendiente') {
      return <Badge className="bg-gray-100 text-gray-500 border-gray-200 border"><Clock size={12} className="mr-1" /> Pendiente</Badge>;
    }
    const pct = e.score !== null && e.score !== undefined ? ` (${e.score}%)` : '';
    if (r === 'Aprobado') {
      return <Badge className="bg-green-50 text-green-600 border-green-200 border"><CheckCircle size={12} className="mr-1" /> Aprobado{pct}</Badge>;
    }
    return <Badge className="bg-red-50 text-red-600 border-red-200 border"><XCircle size={12} className="mr-1" /> Reprobado{pct}</Badge>;
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractor-results-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2B4B8C]">Resultados</h1>
          <p className="text-[#2B4B8C]/60">Resultados de evaluaciones y certificados de tus trabajadores</p>
        </div>
        <Button onClick={handleExportExcel} className="bg-[#2BBAA4] hover:bg-[#239986] text-white" data-testid="export-excel-btn">
          <FileXls size={18} className="mr-2" /> Descargar Excel ({filtered.length})
        </Button>
      </div>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
            <Input placeholder="Buscar por trabajador, capacitación o RUT..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="results-search-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <FilterSelect testId="filter-training" value={filters.training} onChange={setFilter('training')} placeholder="Todas las capacitaciones" options={trainingOptions} />
            </div>
            <FilterSelect testId="filter-cargo" value={filters.cargo} onChange={setFilter('cargo')} placeholder="Todos los cargos" options={cargoOptions} />
            <FilterSelect testId="filter-status" value={filters.status} onChange={setFilter('status')} placeholder="Todos los estados" options={['Inscrito', 'Pendiente', 'Rechazado']} />
            <FilterSelect testId="filter-result" value={filters.result} onChange={setFilter('result')} placeholder="Todos los resultados" options={['Aprobado', 'Reprobado', 'Pendiente', 'Bloqueado']} />
            <Button variant="outline" disabled={!hasFilters} onClick={() => { setFilters(EMPTY_FILTERS); setSearchTerm(''); }} className="border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="clear-filters-btn">
              <Eraser size={16} className="mr-1" /> Limpiar filtros
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-[#2B4B8C]/70">
            <span>Fecha desde</span>
            <Input type="date" value={filters.dateFrom} onChange={(e) => setFilter('dateFrom')(e.target.value)} className="w-auto bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="filter-date-from" />
            <span>hasta</span>
            <Input type="date" value={filters.dateTo} onChange={(e) => setFilter('dateTo')(e.target.value)} className="w-auto bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="filter-date-to" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#2B4B8C]/10">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                <TableHead className="text-[#2B4B8C]/60">Trabajador</TableHead>
                <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Cargo</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Capacitación</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Nº Vez</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Fecha</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                <TableHead className="text-[#2B4B8C]/60">Resultado</TableHead>
                <TableHead className="text-[#2B4B8C]/60 text-right">Certificado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((e) => (
                <TableRow key={e.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]" data-testid={`result-row-${e.id}`}>
                  <TableCell className="text-[#2B4B8C] font-medium">{e.worker_name || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{e.worker_rut || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{e.worker_cargo || '-'}</TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{e.training_name || '-'}</TableCell>
                  <TableCell><CourseCountBadge count={e.course_count} /></TableCell>
                  <TableCell className="text-[#2B4B8C]/60">{fmtDate(e)}</TableCell>
                  <TableCell>
                    {e.status === 'pending' ? (
                      <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 border text-xs">Pendiente</Badge>
                    ) : e.status === 'rejected' ? (
                      <Badge className="bg-red-500/10 text-red-500 border-red-500/20 border text-xs">Rechazado</Badge>
                    ) : (
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/20 border text-xs">Inscrito</Badge>
                    )}
                  </TableCell>
                  <TableCell>{getResultBadge(e)}</TableCell>
                  <TableCell className="text-right">
                    {resultOf(e) === 'Aprobado' ? (
                      <Button variant="ghost" size="sm" onClick={() => handleViewCert(e)} className="text-[#2BBAA4] hover:text-[#239986]" data-testid={`view-cert-${e.id}`}>
                        <Eye size={16} className="mr-1" /> Ver
                      </Button>
                    ) : (
                      <span className="text-xs text-[#2B4B8C]/30">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-[#2B4B8C]/50">No hay resultados disponibles</div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2B4B8C]/10">
              <span className="text-sm text-[#2B4B8C]/60">
                Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} de {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  <CaretLeft size={16} className="mr-1" /> Anterior
                </Button>
                <span className="text-sm text-[#2B4B8C] font-medium px-2">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="border-[#2B4B8C]/10 text-[#2B4B8C]">
                  Siguiente <CaretRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={preview.open} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-white overflow-hidden" data-testid="cert-preview-modal">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#2B4B8C]/10">
            <DialogTitle className="text-[#2B4B8C] text-base pr-6">{preview.title}</DialogTitle>
          </DialogHeader>
          <div className="bg-[#525659] h-[68vh] flex items-center justify-center">
            {preview.url ? (
              <iframe src={preview.url} title="Certificado" className="w-full h-full border-0" data-testid="cert-preview-iframe" />
            ) : (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2BBAA4]" />
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#2B4B8C]/10">
            <Button variant="outline" onClick={closePreview} className="border-[#2B4B8C]/20 text-[#2B4B8C]" data-testid="cert-preview-close">Cerrar</Button>
            <Button onClick={handleDownloadCert} disabled={!preview.cert || downloading} className="bg-[#2BBAA4] hover:bg-[#239986] text-white" data-testid="cert-preview-download">
              <DownloadSimple size={16} className="mr-1.5" /> {downloading ? 'Descargando...' : 'Descargar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
